import { useMemo, useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import {
  useGetProjectsQuery,
  useGetProjectStagesQuery,
  useGetTeamsQuery,
  useGetItemsQuery,
  usePatchContactMutation,
  useGetProjectTypesQuery,
} from 'store/api';
import {
  BadgesCell,
  DateChipCell,
  ColorTypographyCell,
  MailMenuCell,
  RowCell,
  TabIndexTable,
  BackdropLoading,
  AvatarsCell,
  WARNING_DAYS,
  formattedDate,
  AlertDetails,
} from '@centerpiece/components';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge, Box } from '@mui/material';
import {
  FoodBank,
  InfoOutlined,
  FilePresentOutlined,
  PreviewOutlined,
  LocalAtmOutlined,
  CommentOutlined,
  WorkOutlined,
  PeopleOutlineOutlined,
} from '@mui/icons-material';
import {
  Team,
  ProjectStage,
  Contact,
  ProjectState,
  ProjectType,
} from 'store/api/models';
import { ItemStatus } from 'utils/status.utils';
import { useTutorialContext } from 'tutorials/tutorial.context';
import {
  useItemsAtRisk,
  useProjectsIndex,
  useItemCounts,
  useProjectsMsgs,
  useProjectsCmts,
  useNextItems,
} from 'utils/index.utils';
import { useIndexContext } from 'contexts/index.context';
import { CreateProjectDialogue } from 'dialogs/CreateProjectDialogue';
import { ProjectTabDrawer } from 'drawers/ProjectTabDrawer';

export const ProjectsIndexPage = () => {
  const navigate = useNavigate();
  const { contactRecord, cmts, msgs, refetchCount, refetchIndex } =
    useIndexContext();

  // BEGIN USESTATE
  const [projectIndex, setProjectIndex] = useState<{ [key: number]: any }>({});
  const indexProjects = useMemo(
    () => Object.values(projectIndex).filter(i => i.id),
    [projectIndex]
  );
  const [openDialog, setOpenDialog] = useState<string>();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [typeFilter, setTypeFilter] = useState<ProjectType>({
    id: -1,
    name: 'All Types',
  } as ProjectType);
  const [ownershipFilter, setOwnershipFilter] = useState<Team>({
    id: -1,
    name: 'My Projects',
  } as Team);
  const [project, setProject] = useState<{ id: number; tab: string }>();
  const [filter, setFilter] = useState<ProjectState>({
    id: 3,
    name: 'Active',
  } as ProjectStage);
  const [itemOwnershipFilter, setItemOwnershipFilterActual] =
    useState<ItemStatus>({
      id: -2,
      name: 'All Items',
    } as ItemStatus); // active
  const setItemOwnershipFilter = (status: ItemStatus) => {
    if (status.id === -2 || status.id > 0) {
      setItemFilter({ id: -2, name: 'At Risk' });
    }
    setItemOwnershipFilterActual(status);
  };
  const [itemFilter, setItemFilter] = useState<ItemStatus>({
    id: -1,
    name: 'Active',
  } as ItemStatus); // active

  /////// BEGIN QUERIES
  const {
    data: projects = [],
    isLoading: projectListLoading,
    isFetching: projectListFetching,
    refetch: refetchProjects,
  } = useGetProjectsQuery({
    params: {
      optimized: 'true',
      archived: filter?.id === -1 ? true : undefined,
      state: filter?.id > 0 ? filter?.id : undefined,
      contact_id: ownershipFilter.id === -1 ? contactRecord.id : undefined,
      team_ids:
        ownershipFilter.id === -3
          ? 'IS NULL'
          : ownershipFilter.id > 0
          ? ownershipFilter.id
          : undefined,
      type:
        typeFilter.id === -2
          ? 'IS NULL'
          : typeFilter.id > 0
          ? typeFilter.id
          : undefined,
      template: false,
    },
  });
  const { data: riskItems = [] } = useGetItemsQuery(
    {
      params: {
        optimized: 'projectindex',
      },
    },
    { skip: projectListFetching }
  );
  const { data: itemCounts = [] } = useGetItemsQuery(
    {
      params: {
        optimized: 'item_counts',
      },
    },
    { skip: projectListFetching }
  );
  const { data: nextItems = [] } = useGetItemsQuery(
    {
      params: {
        optimized: 'next_item_due',
      },
    },
    { skip: projectListFetching }
  );
  const { data: stages = [] } = useGetProjectStagesQuery({});
  const { data: teams = [] } = useGetTeamsQuery({});
  const { data: ptypes = [] } = useGetProjectTypesQuery({});
  /////// END QUERIES

  useEffect(() => refetchProjects(), [refetchCount]);

  const [patchContact, patchContactResult] = usePatchContactMutation();
  const { state, setState } = useTutorialContext();
  const location = useLocation();
  const initialTab = () => {
    const match = location.hash.match(/at=(\d)/)?.[1];
    let tab = 0;
    if (match) {
      tab = Number(match) || 0;
      window.history.replaceState(null, '', location.pathname);
    }
    return tab;
  };
  useEffect(() => {
    if (state.tourActive) {
      setState(currState => ({
        ...currState,
        run: true,
      }));
    }
    // eslint-disable-next-line
  }, []);
  useProjectsIndex({ projects, setProjectIndex });
  useNextItems({ nextItems, setProjectIndex });
  useItemsAtRisk({ riskItems, setProjectIndex });
  useItemCounts({ itemCounts, setProjectIndex });
  useProjectsMsgs({ msgs, setProjectIndex });
  useProjectsCmts({ cmts, setProjectIndex });
  useEffect(() => {
    if (contactRecord?.form?.authorization === 'Executive') {
      setOwnershipFilter({ id: -2, name: 'All Projects' } as any);
      setItemOwnershipFilter({ id: -2, name: 'All Items' } as any);
      setItemFilter({ id: -2, name: 'At Risk' });
    } else if (
      contactRecord?.form?.authorization === 'Team Leader' &&
      contactRecord?.form?.team
    ) {
      setOwnershipFilter(contactRecord?.form?.team as any);
      setItemOwnershipFilter(contactRecord?.form?.team as any);
      setItemFilter({ id: -2, name: 'At Risk' });
    } else {
      setItemOwnershipFilter({ id: -1, name: 'My Items' } as any);
    }
    if (contactRecord?.form?.project_type) {
      setTypeFilter(contactRecord?.form?.project_type as any);
    }
    // eslint-disable-next-line
  }, [contactRecord]);

  const tableprops: any = useMemo(
    () => ({
      exportFormat: (rows: RowCell[]) => {
        const formattedData: any = rows?.map(row => ({
          'Project Name': row.title.text,
          Financials: row.financials,
          Teams: row.teams?.reduce((acc: string, obj: any) => {
            if (acc && acc !== '') {
              return acc + `, ${obj.name}`;
            } else {
              return `${obj.name} `;
            }
          }, ''),
          'Project Members': row.team?.data?.reduce((acc: string, obj: any) => {
            if (acc && acc !== '') {
              return acc + `, ${obj.firstname} ${obj.lastname}`;
            } else {
              return `${obj.firstname} ${obj.lastname}`;
            }
          }, ''),
          Stage: row.stage_due?.title,
          'Stage Due Date':
            row.stage_due?.due && formattedDate(row.stage_due?.due),
          'Next Item': row.next_item_due?.title,
          'Next Item Due Date':
            row.next_item_due?.due && formattedDate(row.next_item_due?.due),
          'Project Completion': row.due_date && formattedDate(row.due_date),
          'Project Location': row.title.tooltip,
        }));
        return [formattedData, `${ownershipFilter.name}`];
      },
      filters:
        contactRecord?.form?.authorization === 'Executive' ||
        contactRecord?.form?.authorization === 'Organization Member' ||
        ((contactRecord?.form?.authorization === 'Team Leader' ||
          contactRecord?.form?.authorization === 'Team Member') &&
          contactRecord?.form?.team)
          ? [
              {
                options:
                  (contactRecord?.form?.authorization === 'Team Leader' ||
                    contactRecord?.form?.authorization === 'Team Member') &&
                  contactRecord?.form?.team
                    ? [
                        { id: -1, name: 'My Projects' },
                        teams.find(t => t.id == contactRecord?.form?.team?.id),
                      ]
                    : [
                        { id: -1, name: 'My Projects' },
                        { id: -2, name: 'All Projects' },
                        ...(teams || []),
                        { id: -3, name: 'No Team Assigned' },
                      ],
                label: 'Project Relationship',
                defaultOption: ownershipFilter,
                onChange: (event: any, newValue: any) =>
                  setOwnershipFilter(newValue),
                loading: projectListFetching,
              },
              {
                options: [
                  { id: -1, name: 'All Types' },
                  ...(ptypes || []),
                  {
                    id: -2,
                    name: 'No Type Set',
                  },
                ],
                label: 'Project Types',
                defaultOption: typeFilter,
                onChange: (event: any, newValue: any) =>
                  setTypeFilter(newValue),
                loading: projectListFetching,
              },
            ]
          : undefined,
      // checkboxes: true,
      // icons: true,
      // iconHead: <Typography variant='subtitle2'>Type</Typography>,
      actions: [
        // {
        //   name: 'Create Custom Project',
        //   icon: <WorkOutlined />,
        //   onClick: () => setOpenDialog('Custom'),
        // },
        {
          name: 'Create Kitchen Project',
          icon: <FoodBank />,
          onClick: () => setOpenDialog('FES'),
        },
      ],
      onRowClick: (event: any, row: any) => navigate(`project/${row.id}`),
      // isLoading: projectListLoading,
      defaultOrder: 'asc',
      defaultOrderBy: 'stage_due.due',
      hoverCell: true,
      headCells: [
        // {
        //   name: 'icon',
        //   icon: true,
        //   label: 'Class',
        // },
        {
          name: 'title',
          label: 'Project',
          textFilter: 'title.text',
          sort: 'title.text',
          component: ColorTypographyCell,
        },
        {
          name: 'financials',
          label: 'Financials',
          sort: 'financials.sort',
          component: ColorTypographyCell,
        },
        // {
        //   name: 'type_name',
        //   label: 'Type',
        //   checkboxFilter: true,
        // },
        {
          name: 'team',
          checkboxFilter: 'team.team',
          label: 'Team',
          component: AvatarsCell,
        },
        {
          name: 'stage_due',
          label: 'Stage (Due)',
          sort: 'stage_due.due',
          component: DateChipCell,
        },
        // {
        //   name: 'due',
        //   label: 'Stage Due Date',
        //   component: DateChipCell,
        // },
        // {
        //   name: 'next_item',
        //   label: 'Next Item',
        // },
        {
          name: 'next_item_due',
          sort: 'next_item_due.due',
          label: 'Next Item (Due)',
          component: DateChipCell,
        },
        {
          name: 'due_date',
          label: 'Project Completion',
          sort: 'due_date',
          component: ColorTypographyCell,
          render: (row: any, name: any): any => {
            const value = row[name];
            if (value && value !== '') {
              let days = moment(value).diff(moment.now(), 'days');
              return {
                text: moment(value).format('MMM D YYYY'),
                color:
                  days < WARNING_DAYS
                    ? days < 0
                      ? 'error'
                      : 'warning'
                    : 'info',
              };
            } else {
              return { text: '', color: 'info' };
            }
          },
        },
        {
          name: 'risk',
          label: 'Items at Risk',
          hover: true,
          component: BadgesCell,
        },
        {
          name: 'msgs',
          label: '',
          clearFilter: true,
          align: 'right',
          disablePadding: true,
          component: MailMenuCell,
          render: (row: any, name: any): any => {
            return {
              msgs: row.msgs,
              counts: row.counts,
              cmts: row.cmts,
              // ...(row[name] || {}),
              // infoClick: () => setProject(row.id),
              info: {
                // num: itemsWithUnreadCmts?.filter(c => c.project).length,
                onClick: () => setProject({ id: row.id, tab: 'preview' }),
                menuItems: [
                  {
                    icon: <InfoOutlined />,
                    label: 'Project details',
                    onClick: () => {
                      setProject({ id: row.id, tab: 'info' });
                    },
                  },
                  {
                    icon: <PeopleOutlineOutlined />,
                    label: 'Project team',
                    onClick: () => {
                      setProject({ id: row.id, tab: 'team' });
                    },
                  },
                  {
                    icon: <FilePresentOutlined />,
                    label: 'Project files',
                    onClick: () => {
                      setProject({ id: row.id, tab: 'files' });
                    },
                  },
                  {
                    icon: <PreviewOutlined />,
                    label: 'Project preview',
                    onClick: () => {
                      setProject({ id: row.id, tab: 'preview' });
                    },
                  },
                  {
                    icon: (
                      <Badge
                        badgeContent={
                          0
                          // itemsWithUnreadCmts?.filter(c => c.project).length
                        }
                        color="secondary"
                      >
                        <CommentOutlined />
                      </Badge>
                    ),
                    label: 'Project comments',
                    onClick: () => {
                      setProject({ id: row.id, tab: 'comment' });
                    },
                  },
                  {
                    icon: <LocalAtmOutlined />,
                    label: 'Project billings',
                    onClick: () => {
                      setProject({ id: row.id, tab: 'billing' });
                    },
                  },
                ],
              },
            };
          },
        },
      ],
    }),
    [ownershipFilter, typeFilter, projectListFetching, contactRecord]
  );

  return (
    <>
      {/* <ProjectTabDrawer
        open={project > 0}
        initialTab={'preview'}
        previewTab={true}
        id={project}
        onClose={() => {
          setProject(0);
        }}
        afterSave={() => {}}
      /> */}
      <ProjectTabDrawer
        open={project && project?.id > 0}
        initialTab={project?.tab}
        previewTab={true}
        id={project?.id || 0}
        onClose={() => {
          setProject(undefined);
        }}
        afterSave={() => {
          refetchIndex();
        }}
      />
      <CreateProjectDialogue
        open={!!openDialog}
        category={openDialog}
        close={() => setOpenDialog(undefined)}
      />

      <Box
        id={'tab-index-table'}
        sx={{ pt: { xs: 4, md: 0 }, pb: { xs: 15, md: 10 } }}
      >
        {projectListLoading && <BackdropLoading />}
        <TabIndexTable
          filterTitle="Project"
          config={useMemo(
            () => contactRecord?.form?.config || ({ tabs: null } as any),
            [contactRecord?.form?.config]
          )}
          onConfigChange={useCallback(
            async (config: any) => {
              await patchContact({
                id: contactRecord.id,
                form: { config: { ...config } },
              } as Contact);
            },
            [contactRecord.id]
          )}
          configLoading={patchContactResult.isLoading}
          accordian={true} //calendar accordian at the bottome
          initialTab={initialTab()}
          tables={useMemo(
            () => [
              {
                title: 'Total Active',
                key: `all`,
                visible: true,
                ...tableprops,
                defaultOrderBy: 'due_date',
                rows: indexProjects as RowCell[],
              },
              ...stages
                ?.filter(s => s.subscription)
                ?.map(s => ({
                  title: s.name,
                  key: `stage-${s.id}`,
                  visible: true,
                  ...tableprops,
                  headCells: tableprops.headCells.map((c: any) => {
                    if (c.name === 'stage_due') {
                      return { ...c, label: `${s.name} Stage (Due)` };
                    } else {
                      return c;
                    }
                  }),
                  rows:
                    indexProjects?.filter(p => p.stage === s.name) ||
                    ([] as RowCell[]),
                })),
              {
                title:
                  stages?.length > 0
                    ? 'No Stage'
                    : ownershipFilter.name.includes('rojects')
                    ? ownershipFilter.name
                    : `${ownershipFilter.name} projects`,
                key: `other`,
                visible: false,
                ...tableprops,
                defaultOrderBy: 'due_date',
                rows: indexProjects?.filter(p => !p.stage) || ([] as RowCell[]),
              },
            ],
            [stages, tableprops, indexProjects]
          )}
        />
      </Box>
    </>
  );
};
